import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState("M");

  useEffect(() => {
    fetch("/products.json")
      .then((response) => response.json())
      .then((data) => {
        const foundProduct = data.find((p) => p.id === parseInt(id));
        setProduct(foundProduct);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading product:", error);
        setLoading(false);
      });
  }, [id]);

  const increaseQuantity = () => setQuantity((prev) => prev + 1);
  const decreaseQuantity = () => setQuantity((prev) => Math.max(1, prev - 1));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">
          Product Not Found
        </h1>
        <Link to="/products" className="text-primary hover:underline">
          ← Back to Products
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <div className="bg-gradient-light py-4 px-6">
        <div className="container mx-auto">
          <nav className="text-sm text-gray-600">
            <Link to="/" className="text-primary hover:text-primary-dark">
              Home
            </Link>
            <span className="mx-2">/</span>
            <Link
              to="/products"
              className="text-primary hover:text-primary-dark"
            >
              Products
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product Details */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-8 flex items-center justify-center">
            <img
              src={product.images?.[0] || product.image}
              alt={product.name}
              className="max-w-full max-h-[500px] object-contain"
            />
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              {product.name}
            </h1>
            <div className="text-4xl font-bold text-primary mb-6">
              N${product.price}
            </div>

            <p className="text-gray-600 mb-8 leading-relaxed">
              {product.description}
            </p>

            {/* Size Options */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Size:
              </h3>
              <div className="flex flex-wrap gap-3">
                {["S", "M", "L", "XL", "XXL"].map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`px-6 py-3 rounded-lg border-2 font-medium transition-all ${
                      selectedSize === size
                        ? "border-primary bg-primary text-white"
                        : "border-gray-300 text-gray-600 hover:border-primary hover:text-primary"
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Quantity:
              </h3>
              <div className="flex items-center gap-4">
                <button
                  onClick={decreaseQuantity}
                  className="w-12 h-12 border-2 border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) =>
                    setQuantity(Math.max(1, parseInt(e.target.value) || 1))
                  }
                  className="w-20 h-12 text-center border-2 border-gray-300 rounded-lg text-lg font-semibold"
                />
                <button
                  onClick={increaseQuantity}
                  className="w-12 h-12 border-2 border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Link
                to="/contact"
                className="flex-1 bg-gradient-primary text-white py-4 rounded-lg text-center font-semibold hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                Request a Quote
              </Link>
              <Link
                to="/products"
                className="flex-1 border-2 border-gray-300 text-gray-700 py-4 rounded-lg text-center font-semibold hover:border-primary hover:text-primary transition-all duration-300"
              >
                Back to Products
              </Link>
            </div>

            {/* Features */}
            <div className="border-t border-gray-200 pt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Product Features
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-600">
                  <i className="fas fa-check-circle text-primary"></i>
                  Handcrafted with precision
                </li>
                <li className="flex items-center gap-3 text-gray-600">
                  <i className="fas fa-check-circle text-primary"></i>
                  Durable premium materials
                </li>
                <li className="flex items-center gap-3 text-gray-600">
                  <i className="fas fa-check-circle text-primary"></i>
                  Custom sizing available
                </li>
                <li className="flex items-center gap-3 text-gray-600">
                  <i className="fas fa-check-circle text-primary"></i>
                  Made in Namibia
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
